import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * AuthContext — session lives in the `access_token` httpOnly cookie set by the
 * backend on /auth/verify-code or /auth/pin-login. We NEVER store the JWT in
 * localStorage: httpOnly cookies aren't reachable from JS, which eliminates
 * token exfiltration via XSS. On boot we hit /auth/me to hydrate the user.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // 401 is the normal "not logged in yet" case — silent.
            if (e?.response?.status !== 401) {
                console.warn("[Auth] /auth/me failed:", e?.message || e);
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Called by the Login screen after successful verify-code / pin-login.
    // The cookie is already set by that response; we just hydrate local state.
    const login = (_token, userData) => {
        setUser(userData);
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Non-fatal: the server may already have expired the session or
            // be unreachable — we still clear local state below.
            console.warn("[Auth] logout request failed:", e?.message || e);
        }
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, login, logout, refresh, refreshUser: refresh, setUser }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
